import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagLabel,
  useBreakpointValue,
  Box,
  HStack,
  Text,
  Stack,
} from "@chakra-ui/react";
import { InfoIcon, RepeatClockIcon, SettingsIcon } from "@chakra-ui/icons";
import { Theme } from "./Theme";
import Versions from "./Versions";
import ProfilePic from "../img/Jiali.jpeg";
import { useSwipeable } from "react-swipeable";
import { Tips } from "./Tips";
import { FontSizeAndLineHeight } from "./FontSizeAndLineHeight";
import { CopyWithVerseCheckBox } from "./CopyWithVerseCheckBox";
import { ViewMode } from "./ViewMode";
import { WhatsNew } from "./WhatsNew";
import { useAppContext } from "../AppContext";

type SettingsModalProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  initialPage?: number;
};

// Styled Dot Component
const Dot: React.FC<{ active: boolean; onClick: () => void }> = ({ active, onClick }) => (
  <Box // Put a box around the circle to make it easier to click
    w="30px"
    h="30px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    cursor="pointer"
    onClick={onClick}
    sx={{
      WebkitTapHighlightColor: "transparent", // For iOS browsers remove tap highlight
    }}
  >
    <Box
      w="12px"
      h="12px"
      borderRadius="full"
      bg={active ? "cyan.600" : "gray.300"}
      mx="2"
      cursor="pointer"
      transition="background 0.3s"
      onClick={onClick}
    />
  </Box>
);

const FIRSTPAGEMODALHEIGHT = "630px";
const SECONDPAGEMODALHEIGHT = "630px";
const THIRDPAGEMODALHEIGHT = "630px";
const SETTINGSMODALCONTENTPADDING = window.innerHeight < 700 ? "10px" : "20px";

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onOpen, onClose, initialPage = 1 }): JSX.Element => {
  const [rotateState, setRotateState] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [page, setPage] = useState(1);
  const [modalHeight, setModalHeight] = useState<string>(FIRSTPAGEMODALHEIGHT);

  useEffect(() => {
    if (isOpen) {
      setPage(initialPage);
      setModalHeight(
        initialPage === 2 ? SECONDPAGEMODALHEIGHT :
        initialPage === 3 ? THIRDPAGEMODALHEIGHT :
        FIRSTPAGEMODALHEIGHT
      );
    }
  }, [isOpen, initialPage]);
  const [contentOpacity, setContentOpacity] = useState(1); // State to control content opacity
  const [contentTransform, setContentTransform] = useState("scale(1)");
  const { isEnglish } = useAppContext();
  const SETTINGSCHAR = isEnglish ? "Settings" : "設置";
  const TIPSCHAR = isEnglish ? "Tips" : "使用說明";
  const UPDATELOGSCHAR = isEnglish ? "What's New" : "最新功能";

  const modalWidth = useBreakpointValue({ base: "90%", sm: "70%", md: "450px", lg: "450px" });

  const transitionStyle = {
    opacity: contentOpacity,
    transform: contentTransform,
    transition: "opacity 0.5s, transform 0.5s",
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => switchToPage(page + 1 > 3 ? 1 : page + 1),
    onSwipedRight: () => switchToPage(page - 1 < 1 ? 3 : page - 1),
  });

  const handleModalOpen = () => {
    onOpen();
    setRotateState(180);
  };

  const handleModalClose = () => {
    onClose();
    setRotateState(-180);
    setPage(1); // Reset page to 1 when modal is closed
    setModalHeight(FIRSTPAGEMODALHEIGHT);
  };

  const computeRotation = () => {
    if (hovered) return "rotate(180deg)";
    return `rotate(${rotateState}deg)`;
  };

  const switchToPage = (page: number): void => {
    setContentTransform("scale(0.95)"); // set a slight scale down and fade out
    setContentOpacity(0);
    setTimeout(() => {
      if (page === 1) {
        setPage(1);
        setModalHeight(FIRSTPAGEMODALHEIGHT);
      } else if (page === 2) {
        setPage(2);
        setModalHeight(SECONDPAGEMODALHEIGHT);
      } else if (page === 3) {
        setPage(3);
        setModalHeight(THIRDPAGEMODALHEIGHT);
      }
      setContentOpacity(1); // restore content opacity after delay
      setContentTransform("scale(1)"); // restore scale to original size after delay
    }, 300);
  };

  const TipsButton = (): JSX.Element => {
    return (
      <Button flex="1" onClick={() => switchToPage(2)} leftIcon={<InfoIcon />}>
        {TIPSCHAR}
      </Button>
    );
  };

  const SettingsButton = (): JSX.Element => {
    return (
      <Button flex="1" onClick={() => switchToPage(1)} leftIcon={<SettingsIcon />}>
        {SETTINGSCHAR}
      </Button>
    );
  };

  const UpdateLogsButton = (): JSX.Element => {
    return (
      <Button flex="1" onClick={() => switchToPage(3)} leftIcon={<RepeatClockIcon />}>
        {UPDATELOGSCHAR}
      </Button>
    );
  };

  return (
    <>
      <Button
        onClick={handleModalOpen}
        p={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        w={6}
      >
        <SettingsIcon boxSize={5} transition="transform 0.8s" transform={computeRotation()} />
      </Button>

      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
        <ModalOverlay />
        <ModalContent
          maxW={modalWidth}
          minH={FIRSTPAGEMODALHEIGHT}
          h={modalHeight}
          transition="height 0s ease-in-out"
          position="relative"
        >
          <ModalHeader fontSize="2xl" margin={0} h={"50px"}>
            {page === 1 ? (
              <HStack spacing={"6px"}>
                <SettingsIcon h={"20px"} mt={"2px"} />
                <Text as={"span"}>{SETTINGSCHAR}</Text>
              </HStack>
            ) : page === 2 ? (
              <HStack spacing={"6px"}>
                <InfoIcon h={"20px"} mt={"2px"} />
                <Text as={"span"}>{TIPSCHAR}</Text>
              </HStack>
            ) : (
              <HStack spacing={"6px"}>
                <RepeatClockIcon h={"20px"} mt={"2px"} />
                <Text as={"span"}>{UPDATELOGSCHAR}</Text>
              </HStack>
            )}
          </ModalHeader>
          <ModalCloseButton mt="8px" />
          <Flex justifyContent="center" gap="10px">
            <Dot active={page === 1} onClick={() => switchToPage(1)} />
            <Dot active={page === 2} onClick={() => switchToPage(2)} />
            <Dot active={page === 3} onClick={() => switchToPage(3)} />
          </Flex>
          <ModalBody p={SETTINGSMODALCONTENTPADDING} {...handlers} style={transitionStyle}>
            {page === 1 ? (
              <Flex direction="column" gap="7">
                <Stack maxH="380px" overflowY={"scroll"} spacing="6">
                  {/* First Row: Theme with 2 theme buttons */}
                  <Theme />
                  {/* Second Row: Version with React selector */}
                  <Versions />
                  <ViewMode />
                  <CopyWithVerseCheckBox />
                  <FontSizeAndLineHeight />
                </Stack>
                <HStack w={"100%"}>
                  <TipsButton />
                  <UpdateLogsButton />
                </HStack>
              </Flex>
            ) : page === 2 ? (
              <Flex direction="column" gap="7">
                <Tips />
                <HStack w={"100%"}>
                  <SettingsButton />
                  <UpdateLogsButton />
                </HStack>
              </Flex>
            ) : (
              <Flex direction="column" gap="7">
                <WhatsNew />
                <HStack w={"100%"}>
                  <SettingsButton />
                  <TipsButton />
                </HStack>
              </Flex>
            )}
          </ModalBody>

          <ModalFooter
            w={"100%"}
            display="flex"
            justifyContent="space-between"
            style={transitionStyle}
            position="absolute"
            bottom="0px"
          >
            <Tag size="sm" colorScheme="cyan" borderRadius="full">
              <Avatar src={ProfilePic} size="xs" ml={-1} mr={2} mt={1} mb={1} />
              <TagLabel>{isEnglish ? `Developed by Jiali Liang` : `由加力設計以及開發`} </TagLabel>
            </Tag>
            <Button variant="ghost" onClick={handleModalClose}>
              {isEnglish ? "Close" : "關閉"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
